import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { NetPingDeviceFindInterface } from '@zaoblako/shared/modules/pacs/dtos/net-ping-device/net-ping-device-find.dto';
import {
  NetPingDeviceFormDto,
  type NetPingDeviceFormInterface
} from '@zaoblako/shared/modules/pacs/dtos/net-ping-device/net-ping-device-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { NetPingDeviceService } from '@zaoblako/web/core/services/control/net-ping-device.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { RoomSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-single-select/room-single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-net-ping-device-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    CardModule,
    JoinPipe,
    InputTextComponent,
    RoomSingleSelectComponent,
    ButtonComponent,
    SelectButtonModule,
    FormsModule,
    CheckboxModule,
    InputNumberModule,
    DirectoryCommonHistoryComponent
  ],
  templateUrl: './net-ping-device-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NetPingDeviceFormComponent extends BaseFormComponent<NetPingDeviceFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public dto = NetPingDeviceFormDto;
  public device: NetPingDeviceFormInterface = <NetPingDeviceFormInterface>{};

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  public constructor(
    public readonly formStoreService: FormStoreService,
    public readonly netPingDeviceService: NetPingDeviceService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.netPingDeviceService
      .findById<NetPingDeviceFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (device) => {
          this.device = device;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveDevice() {
    if (this.checkValidate(this.device).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.netPingDeviceService
          .update<NetPingDeviceFormInterface, NetPingDeviceFindInterface>(this.device)
          .subscribe({
            next: (device) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Датчик "${this.device.name}" обновлен`);
              this.formStoreService.saveNext(device, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.netPingDeviceService.create<NetPingDeviceFindInterface>(this.device).subscribe({
          next: (device) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Датчик создан');
            this.formStoreService.closeSidebar(this.index);
            this.formStoreService.saveNext(device, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteDevice() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить датчик "${this.device.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.netPingDeviceService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Датчик "${this.device.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
